import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BookOpen, Trash2, Upload, Gift } from "lucide-react";
import api, { apiError } from "../lib/api";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";

export default function AdminEbooks() {
  const [ebooks, setEbooks] = useState([]);
  const [form, setForm] = useState({ title: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [credit, setCredit] = useState({ email: "", delta: 1 });
  const fileRef = useRef(null);

  const load = async () => {
    try { setEbooks((await api.get("/admin/ebooks")).data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a PDF file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("file", file);
      await api.post("/admin/ebooks", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Ebook added to the reward library");
      setForm({ title: "", description: "" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
  };

  const toggle = async (b) => {
    try { await api.put(`/admin/ebooks/${b.id}`, { active: !b.active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}" permanently? Members who already claimed it can still download their copy.`)) return;
    try { await api.delete(`/admin/ebooks/${b.id}`); toast.success("Ebook deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const adjustCredit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/admin/referrals/adjust", { email: credit.email, delta: parseInt(credit.delta, 10) || 0 });
      toast.success(`Done — member now has ${r.data.credits} credit(s)`);
      setCredit({ email: "", delta: 1 });
    } catch (e2) { toast.error(apiError(e2)); }
  };

  return (
    <div data-testid="admin-ebooks">
      <h2 className="font-display font-bold text-xl flex items-center gap-2 mb-2"><BookOpen size={20} /> Referral Reward Ebooks</h2>
      <p className="text-sm text-slate-500 mb-6 max-w-2xl">Members earn 1 credit each time a referred friend makes a paid purchase, and spend credits on these ebooks. Deactivate an ebook to hide it without deleting it.</p>

      <form onSubmit={upload} className="bg-white border border-slate-200 rounded-2xl p-5 mb-8 grid sm:grid-cols-2 gap-3" data-testid="ebook-upload-form">
        <input required className={inputCls} placeholder="Ebook title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ebook-title-input" />
        <input className={inputCls} placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ebook-desc-input" />
        <input ref={fileRef} type="file" accept=".pdf" className="text-sm" data-testid="ebook-file-input" />
        <button disabled={busy} className="bg-emerald-700 text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-emerald-800 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="ebook-upload-btn">
          <Upload size={14} /> {busy ? "Uploading…" : "Add ebook (PDF)"}
        </button>
      </form>

      <div className="space-y-2 mb-10">
        {ebooks.map((b) => (
          <div key={b.id} className="bg-white border border-slate-200 rounded-2xl px-5 py-4 flex flex-wrap items-center gap-3" data-testid={`ebook-row-${b.id}`}>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-sm">{b.title}</p>
              <p className="text-xs text-slate-500 truncate">{b.description}</p>
            </div>
            <span className="text-xs text-slate-400 shrink-0">{b.claims} claim{b.claims === 1 ? "" : "s"}</span>
            <button onClick={() => toggle(b)} data-testid={`ebook-toggle-${b.id}`}
              className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full shrink-0 ${b.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
              {b.active ? "Active" : "Hidden"}
            </button>
            <button onClick={() => remove(b)} className="text-red-500 hover:text-red-700 shrink-0" data-testid={`ebook-delete-${b.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
        {ebooks.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No ebooks yet — add your first reward above.</p>}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 max-w-xl" data-testid="credit-adjust-card">
        <h3 className="font-display font-bold flex items-center gap-2"><Gift size={16} /> Manually grant / remove credits</h3>
        <p className="text-xs text-slate-500 mt-1">Useful for goodwill gestures or fixing issues. Use a negative number to remove.</p>
        <form onSubmit={adjustCredit} className="flex flex-col sm:flex-row gap-2 mt-3">
          <input required type="email" className={inputCls} placeholder="Member email" value={credit.email} onChange={(e) => setCredit({ ...credit, email: e.target.value })} data-testid="credit-email-input" />
          <input required type="number" className={`${inputCls} sm:w-24`} value={credit.delta} onChange={(e) => setCredit({ ...credit, delta: e.target.value })} data-testid="credit-delta-input" />
          <button className="shrink-0 bg-slate-900 text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-emerald-700 transition-colors" data-testid="credit-adjust-btn">Apply</button>
        </form>
      </div>
    </div>
  );
}
